import React, { useState, useEffect } from 'react';
import * as S from './styles';
import {
  CircularProgress, Grid, Typography, Button, Card, CardContent,
  Box, Divider, Tabs, Tab, Chip, Paper, Stack, Avatar, Tooltip, useTheme, useMediaQuery,
  List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { api } from '@/services/api';
import WhoWins from '@/components/WhoWins';
import {
  MdSportsScore, MdSportsVolleyball, MdOutlinePercent,
  MdStadium, MdOutlineQueryStats, MdTimer, MdAlarm,
  MdPerson, MdInfo, MdSportsSoccer, MdKeyboardArrowRight, MdAccessTime
} from 'react-icons/md';
import { BsClock, BsTrophy, BsCheckCircle, BsClockHistory } from 'react-icons/bs';
import { FaMoneyBillWave, FaChartLine, FaExclamationTriangle, FaRegCalendarAlt } from 'react-icons/fa';

interface TeamLogos {
  [key: string]: string;
}

interface DetailedAnalysis {
  timestamp: string;
  matches: DetailedMatch[];
}

interface DetailedMatch {
  matchId: string;
  league: {
    id: string;
    name: string;
    country: string;
    logo: string;
  };
  homeTeam: {
    id: string;
    name: string;
    logo: string;
    recentForm: string;
  };
  awayTeam: {
    id: string;
    name: string;
    logo: string;
    recentForm: string;
  };
  date: string;
  time: string;
  status: string;
  score: {
    home: number;
    away: number;
  };
  isLive: boolean;
  matchMinute: number;
  liveOpportunities: LiveOpportunity[];
  preMatchOpportunities: PreMatchOpportunity[];
  performance: MatchPerformance;
  h2h?: {
    totalMatches: number;
    homeTeamWins: number;
    awayTeamWins: number;
    draws: number;
    lastMatches: {
      date: string;
      homeTeam: string;
      awayTeam: string;
      score: {
        home: number;
        away: number;
      };
    }[];
  };
  predictions: {
    winProbability: {
      home: number;
      draw: number;
      away: number;
    };
    btts: {
      yes: number;
      no: number;
    };
    overUnder: {
      over15: number;
      over25: number;
      over35: number;
    };
    correctScore: {
      score: string;
      probability: number;
    }[];
    expectedGoals: {
      home: number;
      away: number;
    };
  };
  mlPrediction?: any;
}

interface MatchPerformance {
  possession: {
    home: number;
    away: number;
  };
  shots: {
    home: {
      total: number;
      onTarget: number;
      offTarget: number;
    };
    away: {
      total: number;
      onTarget: number;
      offTarget: number;
    };
  };
  passes: {
    home: {
      total: number;
      accuracy: number;
    };
    away: {
      total: number;
      accuracy: number;
    };
  };
  corners: {
    home: number;
    away: number;
  };
  fouls: {
    home: number;
    away: number;
  };
  cards: {
    home: {
      yellow: number;
      red: number;
    };
    away: {
      yellow: number;
      red: number;
    };
  };
}

interface PreMatchOpportunity extends LiveOpportunity {
  historicalConfidence: number;
  historicalData: {
    pastMatches: number;
    winRate: number;
    bttsRate: number;
    avgGoals: number;
  };
}

interface DebugData {
  timestamp: string;
  requestParams: Record<string, any>;
  rawApiData?: any;
  calculatedFeatures?: Record<string, any>;
  matchPredictions?: any[];
  bettingOpportunities?: any[];
  playerAnalysis?: Record<string, any[]>;
  performanceMetrics?: Record<string, any>;
  processingTimeMs: number;
  version?: string;
  liveBettingOpportunities?: LiveOpportunity[];
  leagueStats?: {
    leagues: Array<any>;
    betTypePerformance: Record<string, any>;
    modelPerformance: Record<string, any>;
  };
  lastUpdated?: string;
  scheduledBettingOpportunities?: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`smart-tickets-tabpanel-${index}`}
      aria-labelledby={`smart-tickets-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface LiveOpportunity {
  id: string;
  matchId: string;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  league: {
    name: string;
    id: string;
  };
  matchDateTime: string;
  matchEndTime?: string;
  matchStatus: string;
  currentScore: {
    home: number;
    away: number;
  };
  betType: string;
  betOption: string;
  odds: string | number;
  confidenceScore: number;
  valueAssessment: number;
  impliedProbability: string | number;
  source: string;
  reasons: string[];
  riskLevel: string;
  isLive: boolean;
  predictionMethod: string;
  verificationStatus: string;
  tracked: boolean;
  finished?: boolean;
}

// Adicionar logos dos times
const TEAM_LOGOS: TeamLogos = {
  "Manchester United": "https://upload.wikimedia.org/wikipedia/en/7/7a/Manchester_United_FC_crest.svg",
  "Manchester City": "https://upload.wikimedia.org/wikipedia/en/e/eb/Manchester_City_FC_badge.svg",
  "Arsenal": "https://upload.wikimedia.org/wikipedia/en/5/53/Arsenal_FC.svg",
  "Chelsea": "https://upload.wikimedia.org/wikipedia/en/c/cc/Chelsea_FC.svg",
  "Liverpool": "https://upload.wikimedia.org/wikipedia/en/0/0c/Liverpool_FC.svg",
  "Tottenham": "https://upload.wikimedia.org/wikipedia/en/b/b4/Tottenham_Hotspur.svg",
  "Barcelona": "https://upload.wikimedia.org/wikipedia/en/4/47/FC_Barcelona_%28crest%29.svg",
  "Real Madrid": "https://upload.wikimedia.org/wikipedia/en/5/56/Real_Madrid_CF.svg",
  "Bayern Munich": "https://upload.wikimedia.org/wikipedia/commons/1/1b/FC_Bayern_M%C3%BCnchen_logo_%282017%29.svg",
  "PSG": "https://upload.wikimedia.org/wikipedia/en/a/a7/Paris_Saint-Germain_F.C..svg",
  "Juventus": "https://upload.wikimedia.org/wikipedia/commons/1/15/Juventus_FC_2017_logo.svg",
  "AC Milan": "https://upload.wikimedia.org/wikipedia/commons/d/d0/AC_Milan_logo.svg",
  "Inter Milan": "https://upload.wikimedia.org/wikipedia/commons/0/05/FC_Internazionale_Milano_2021.svg",
  "Borussia Dortmund": "https://upload.wikimedia.org/wikipedia/commons/6/67/Borussia_Dortmund_logo.svg"
};

// Função para pegar a logo do time ou retornar uma imagem padrão
const getTeamLogo = (teamName: string): string => {
  return TEAM_LOGOS[teamName as keyof typeof TEAM_LOGOS] ||
    "https://placehold.co/40?text=" + encodeURIComponent(teamName.charAt(0));
};

// Função para calcular a hora de término estimada (2 horas após o início)
const getEstimatedEndTime = (startTime: string): string => {
  try {
    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 horas depois
    return endDate.toISOString();
  } catch (e) {
    return '';
  }
};

// Calcula probabilidades com base no placar atual e minuto do jogo
const calculateProbabilities = (game: any) => {
  const homeScore = parseInt(game.match_hometeam_score) || 0;
  const awayScore = parseInt(game.match_awayteam_score) || 0;
  const scoreDifference = homeScore - awayScore;
  const minute = parseInt(game.match_live) || 45; // Minuto do jogo ou padrão 45
  const isSecondHalf = minute > 45;

  // Base probabilidades no placar atual e tempo de jogo
  let homeWinProb, drawProb, awayWinProb;

  if (scoreDifference > 1) {
    // Time da casa ganhando por 2+ gols
    homeWinProb = 70 + (10 * Math.min(scoreDifference - 1, 3)) - (minute / 10);
    drawProb = 20 - (5 * Math.min(scoreDifference - 1, 3)) + (minute / 15);
    awayWinProb = 10 - (5 * Math.min(scoreDifference - 1, 2)) + (minute / 20);
  } else if (scoreDifference === 1) {
    // Time da casa ganhando por 1 gol
    homeWinProb = 55 - (minute / 12);
    drawProb = 30 + (minute / 15);
    awayWinProb = 15 + (minute / 20);
  } else if (scoreDifference === 0) {
    // Empate
    homeWinProb = 40 - (minute / 15);
    drawProb = 35 + (minute / 12);
    awayWinProb = 25 + (minute / 30);
  } else if (scoreDifference === -1) {
    // Time visitante ganhando por 1 gol
    homeWinProb = 25 - (minute / 30);
    drawProb = 30 + (minute / 15);
    awayWinProb = 45 + (minute / 12);
  } else {
    // Time visitante ganhando por 2+ gols
    homeWinProb = 15 - (5 * Math.min(Math.abs(scoreDifference) - 1, 2)) - (minute / 20);
    drawProb = 25 - (5 * Math.min(Math.abs(scoreDifference) - 1, 3)) - (minute / 15);
    awayWinProb = 60 + (10 * Math.min(Math.abs(scoreDifference) - 1, 3)) + (minute / 10);
  }

  // Ajuste final para garantir que a soma seja 100%
  const total = homeWinProb + drawProb + awayWinProb;
  homeWinProb = (homeWinProb / total) * 100;
  drawProb = (drawProb / total) * 100;
  awayWinProb = (awayWinProb / total) * 100;

  // Calcular probabilidade de mais de 2.5 gols
  const totalGoals = homeScore + awayScore;
  const goalRate = totalGoals / (minute / 90); // Taxa de gols por jogo completo
  const remainingTimeRatio = (90 - minute) / 90;
  const expectedRemainingGoals = goalRate * remainingTimeRatio * 90;
  const over25Prob = totalGoals >= 3 ?
    100 :
    Math.min(90, Math.max(10, (totalGoals + expectedRemainingGoals > 2.5 ? 70 : 35)));

  return {
    homeWinProb: Math.round(homeWinProb),
    drawProb: Math.round(drawProb),
    awayWinProb: Math.round(awayWinProb),
    over25Prob: Math.round(over25Prob),
    under25Prob: Math.round(100 - over25Prob),
    bttsProb: calculateBTTSProbability(game, minute)
  };
};

// Calcular probabilidade de ambas as equipes marcarem
const calculateBTTSProbability = (game: any, minute: number) => {
  const homeScore = parseInt(game.match_hometeam_score) || 0;
  const awayScore = parseInt(game.match_awayteam_score) || 0;
  const bothTeamsScored = homeScore > 0 && awayScore > 0;

  if (bothTeamsScored) {
    return 100; // Já aconteceu
  }

  if (homeScore === 0 && awayScore === 0) {
    // Nenhum time marcou ainda
    return Math.max(20, 80 - (minute / 2)); // Diminui com o tempo
  } else {
    // Um time marcou, outro não
    const remainingMinutes = 90 - minute;
    return Math.max(10, Math.min(90, remainingMinutes * 1.5));
  }
};

// Definir odds com base na probabilidade
const calculateOdds = (probability: number) => {
  // Odd = 1 / (probabilidade / 100) com margem de 10%
  const odd = 1 / (probability / 100) * 1.1;
  return parseFloat(odd.toFixed(2));
};

// Gerar razões personalizadas com base no jogo
const generateReasons = (game: any, betType: string, homeWinProb: number, drawProb: number, awayWinProb: number) => {
  const reasons = [];
  const homeScore = parseInt(game.match_hometeam_score) || 0;
  const awayScore = parseInt(game.match_awayteam_score) || 0;
  const minute = parseInt(game.match_live) || 45;

  if (betType === "Resultado Final") {
    if (homeWinProb > 60) {
      reasons.push(`${game.match_hometeam_name} tem ${homeWinProb}% de chance de vitória`);
      if (homeScore > awayScore) {
        reasons.push(`${game.match_hometeam_name} já está à frente no placar`);
      }
    } else if (awayWinProb > 60) {
      reasons.push(`${game.match_awayteam_name} tem ${awayWinProb}% de chance de vitória`);
      if (awayScore > homeScore) {
        reasons.push(`${game.match_awayteam_name} já está à frente no placar`);
      }
    } else if (drawProb > 45) {
      reasons.push(`Empate tem alta probabilidade de ${drawProb}%`);
      if (homeScore === awayScore) {
        reasons.push("As equipes estão empatadas neste momento");
      }
    }
  } else if (betType === "Mais/Menos Gols") {
    const totalGoals = homeScore + awayScore;
    if (totalGoals >= 2) {
      reasons.push(`Jogo já tem ${totalGoals} gols em ${minute} minutos`);
      reasons.push("Ritmo de jogo favorece mais gols");
    } else {
      reasons.push(`${minute > 60 ? "Apesar do" : "Considerando o"} tempo decorrido (${minute}'), a probabilidade de mais gols é significativa`);
    }
  }

  // Adicionar razão genérica se necessário
  if (reasons.length < 2) {
    reasons.push("Análise estatística mostra valor na aposta");
  }

  return reasons;
};

// Determinar risco com base na probabilidade e outros fatores
const calculateRiskLevel = (probability: number, minute: number) => {
  if (probability > 75) return "Baixo";
  if (probability > 55) return "Médio";

  // Quanto mais tarde no jogo, maior o risco para probabilidades baixas
  if (minute > 70 && probability < 40) return "Alto";

  return "Médio";
};

export default function SmartTickets() {
  const [debugData, setDebugData] = useState<DebugData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Estado para análises detalhadas
  const [detailedAnalysis, setDetailedAnalysis] = useState<DetailedAnalysis | null>(null);
  const [isLoadingDetailedAnalysis, setIsLoadingDetailedAnalysis] = useState<boolean>(false);
  const [detailedAnalysisError, setDetailedAnalysisError] = useState<string | null>(null);

  useEffect(() => {
    fetchDebugData();
  }, []);

  const fetchDebugData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      try {
        const response = await api.get('/api/v1/smart-tickets/debug');
        if (response && response.data) {
          const processedData = { ...response.data };

          // Processamento dos dados
          if (!processedData.timestamp) {
            processedData.timestamp = new Date().toISOString();
          }

          if (!processedData.requestParams) {
            processedData.requestParams = {};
          }

          if (!processedData.processingTimeMs) {
            processedData.processingTimeMs = 0;
          }

          // Processamento de oportunidades ao vivo
          if (processedData.liveBettingOpportunities && Array.isArray(processedData.liveBettingOpportunities)) {
            processedData.liveBettingOpportunities = processedData.liveBettingOpportunities.map((opp: any) => ({
              ...opp,
              homeTeamLogo: opp.homeTeamLogo || getTeamLogo(opp.homeTeamName),
              awayTeamLogo: opp.awayTeamLogo || getTeamLogo(opp.awayTeamName),
              matchEndTime: opp.matchEndTime || getEstimatedEndTime(opp.matchDateTime),
              isLive: opp.isLive !== undefined ? opp.isLive : true,
              confidenceScore: opp.confidenceScore || 0,
              valueAssessment: opp.valueAssessment || 0,
              reasons: Array.isArray(opp.reasons) ? opp.reasons : [],
              riskLevel: opp.riskLevel || "Médio",
              tracked: opp.tracked !== undefined ? opp.tracked : false,
              finished: opp.finished !== undefined ? opp.finished : false,
              currentScore: opp.currentScore || { home: 0, away: 0 }
            }));
          } else {
            processedData.liveBettingOpportunities = [];
          }

          // Processamento de oportunidades agendadas
          if (processedData.scheduledBettingOpportunities && Array.isArray(processedData.scheduledBettingOpportunities)) {
            processedData.bettingOpportunities = processedData.scheduledBettingOpportunities.map((opp: any) => ({
              ...opp,
              homeTeamLogo: opp.homeTeamLogo || getTeamLogo(opp.homeTeamName),
              awayTeamLogo: opp.awayTeamLogo || getTeamLogo(opp.awayTeamName)
            }));
          } else if (!processedData.bettingOpportunities) {
            processedData.bettingOpportunities = [];
          }

          console.log('Dados processados antes de definir o estado:', processedData);
          setDebugData(processedData as DebugData);
          setIsLoading(false);
          return;
        } else {
          throw new Error('Resposta inválida do servidor');
        }
      } catch (error) {
        console.error('Erro ao obter dados de debug:', error);
        setError("Erro ao carregar dados. Por favor, tente novamente.");
        setIsLoading(false);
      }
    } catch (generalErr: any) {
      setError("Erro ao carregar dados: " + (generalErr.message || "Erro desconhecido"));
      console.error("Erro geral ao buscar dados:", generalErr);
      setIsLoading(false);
    }
  };

  // Função para formatar um valor como número com 2 casas decimais
  const formatNumber = (value: any): string => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    } else if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? '0.00' : num.toFixed(2);
    }
    return '0.00';
  };

  // Função para obter valores de gols esperados de forma segura
  const getExpectedGoals = (prediction: any) => {
    // Tentar diferentes estruturas possíveis para expected goals

    // Se tiver estrutura expectedGoals.home
    if (prediction.expectedGoals &&
      (typeof prediction.expectedGoals.home === 'number' ||
        typeof prediction.expectedGoals.home === 'string')) {
      return {
        home: formatNumber(prediction.expectedGoals.home),
        away: formatNumber(prediction.expectedGoals.away)
      };
    }

    // Se tiver estrutura expectedGoalsHome
    if ((typeof prediction.expectedGoalsHome === 'number' ||
      typeof prediction.expectedGoalsHome === 'string') &&
      (typeof prediction.expectedGoalsAway === 'number' ||
        typeof prediction.expectedGoalsAway === 'string')) {
      return {
        home: formatNumber(prediction.expectedGoalsHome),
        away: formatNumber(prediction.expectedGoalsAway)
      };
    }

    // Valor padrão
    return {
      home: '0.00',
      away: '0.00'
    };
  };

  // Handler para mudança de tab
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Define a cor de risco baseada no nível
  const getRiskColor = (risk: string) => {
    switch (risk?.toLowerCase()) {
      case 'baixo': return 'success';
      case 'médio': return 'warning';
      case 'alto': return 'error';
      default: return 'default';
    }
  };

  // Formatar a data e horário para exibição
  const formatDateTime = (dateTimeStr: string): string => {
    try {
      const dt = new Date(dateTimeStr);
      return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  // Renderiza conteúdo de oportunidades ao vivo
  const renderLiveOpportunities = () => {
    if (isLoading) {
      return (
        <S.LoadingContainer>
          <CircularProgress />
          <Typography variant="body1">Carregando oportunidades de apostas ao vivo...</Typography>
        </S.LoadingContainer>
      );
    }

    if (error) {
      return (
        <S.ErrorContainer>
          <Typography variant="body1" color="error">
            Erro ao carregar dados: {error}
          </Typography>
          <Button variant="contained" onClick={fetchDebugData}>
            Tentar Novamente
          </Button>
        </S.ErrorContainer>
      );
    }

    if (!debugData || !debugData.liveBettingOpportunities || debugData.liveBettingOpportunities.length === 0) {
      return (
        <S.NoDataContainer>
          <MdInfo size={48} color="#757575" />
          <Typography variant="h6" color="text.secondary">
            Nenhuma oportunidade ao vivo disponível no momento
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Não há jogos ao vivo com oportunidades de apostas identificadas pelo nosso sistema.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchDebugData}
            startIcon={<MdOutlineQueryStats />}
          >
            TENTAR NOVAMENTE
          </Button>
        </S.NoDataContainer>
      );
    }

    const opportunities = debugData.liveBettingOpportunities;

    return (
      <>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" component="h2" sx={{ color: '#ffffff' }}>
            <MdSportsScore style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Total de oportunidades ao vivo: {opportunities.length}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {opportunities.map((opportunity, index) => {
            const valueScore = Number(opportunity.valueAssessment);
            const confidenceScore = Number(opportunity.confidenceScore);

            // Formatar horários
            const startTime = formatDateTime(opportunity.matchDateTime);
            const endTime = formatDateTime(opportunity.matchEndTime || '');

            return (
              <Grid item xs={12} sm={6} md={4} key={opportunity.id || index}>
                <S.OpportunityCardLive elevation={3}>
                  {opportunity.finished && (
                    <S.FinishedGameOverlay>
                      <span>Finalizado</span>
                    </S.FinishedGameOverlay>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MdStadium style={{ marginRight: '5px', color: '#8a8a8a' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: '120px', sm: '150px', md: '200px' }
                        }}
                      >
                        {opportunity.league?.name || 'Liga não especificada'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {opportunity.isLive ? (
                        <S.LiveBadge>Ao vivo</S.LiveBadge>
                      ) : (
                        <S.FinishedBadge>Finalizado</S.FinishedBadge>
                      )}
                      <Chip
                        size="small"
                        label={opportunity.riskLevel}
                        sx={{
                          backgroundColor: getRiskColor(opportunity.riskLevel) === 'success' ? '#4caf50' :
                            getRiskColor(opportunity.riskLevel) === 'warning' ? '#ff9800' : '#f44336',
                          color: '#fff',
                          fontSize: '0.7rem',
                          height: '20px',
                          fontWeight: 'bold'
                        }}
                      />
                    </Stack>
                  </Box>

                  <S.MatchContainer>
                    <S.TeamInfoContainer>
                      <Avatar
                        src={opportunity.homeTeamLogo || 'https://placehold.co/40?text=Home'}
                        alt={opportunity.homeTeamName}
                        sx={{ width: 40, height: 40 }}
                      />
                      <S.TeamName>{opportunity.homeTeamName}</S.TeamName>
                    </S.TeamInfoContainer>

                    <S.ScoreBox>
                      <Typography variant="h5" component="span" sx={{
                        fontWeight: 'bold',
                        color: '#ffffff',
                        textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        fontSize: { xs: '1.2rem', sm: '1.4rem' }
                      }}>
                        {opportunity.currentScore?.home || 0}
                      </Typography>
                      <Typography variant="h5" component="span" sx={{
                        mx: 1,
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: { xs: '1.2rem', sm: '1.4rem' }
                      }}>
                        x
                      </Typography>
                      <Typography variant="h5" component="span" sx={{
                        fontWeight: 'bold',
                        color: '#ffffff',
                        textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        fontSize: { xs: '1.2rem', sm: '1.4rem' }
                      }}>
                        {opportunity.currentScore?.away || 0}
                      </Typography>
                    </S.ScoreBox>

                    <S.TeamInfoContainer>
                      <Avatar
                        src={opportunity.awayTeamLogo || 'https://placehold.co/40?text=Away'}
                        alt={opportunity.awayTeamName}
                        sx={{ width: 40, height: 40 }}
                      />
                      <S.TeamName>{opportunity.awayTeamName}</S.TeamName>
                    </S.TeamInfoContainer>
                  </S.MatchContainer>

                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BsClock style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {startTime}
                      </Typography>
                    </Box>

                    {opportunity.matchEndTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BsClockHistory style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                          Término est.: {endTime}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
                      Tipo de Aposta
                    </Typography>
                    <Paper sx={{ p: 1, backgroundColor: 'rgba(30, 40, 50, 0.9)', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: '#ffffff' }}>
                        {opportunity.betType} - <strong>{opportunity.betOption}</strong>
                      </Typography>
                    </Paper>
                  </Box>

                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <S.MetricBox>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                          Odds
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4fc3f7' }}>
                          {formatNumber(opportunity.odds)}
                        </Typography>
                      </S.MetricBox>
                    </Grid>

                    <Grid item xs={4}>
                      <S.MetricBox>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                          Valor
                        </Typography>
                        <S.ValueIndicator value={valueScore}>
                          {formatNumber(valueScore)}%
                        </S.ValueIndicator>
                      </S.MetricBox>
                    </Grid>

                    <Grid item xs={4}>
                      <S.MetricBox>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                          Confiança
                        </Typography>
                        <S.ValueIndicator value={confidenceScore}>
                          {formatNumber(confidenceScore)}%
                        </S.ValueIndicator>
                      </S.MetricBox>
                    </Grid>
                  </Grid>

                  {opportunity.reasons && opportunity.reasons.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
                        Justificativas
                      </Typography>
                      <S.ReasonsContainer>
                        <List dense disablePadding>
                          {opportunity.reasons.map((reason, idx) => (
                            <ListItem key={idx} disablePadding sx={{ alignItems: 'flex-start', mb: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 24, color: '#4fc3f7', mt: 0.5 }}>
                                <MdKeyboardArrowRight />
                              </ListItemIcon>
                              <ListItemText
                                primary={reason}
                                primaryTypographyProps={{
                                  variant: 'caption',
                                  sx: { color: 'rgba(255, 255, 255, 0.8)' }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </S.ReasonsContainer>
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: '0.65rem' }}>
                        Risco: <span style={{
                          color: getRiskColor(opportunity.riskLevel) === 'success' ? '#4caf50' :
                            getRiskColor(opportunity.riskLevel) === 'warning' ? '#ff9800' : '#f44336'
                        }}>{opportunity.riskLevel}</span>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: '0.65rem' }}>
                        Monitorado: {opportunity.tracked ? 'Sim' : 'Não'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: '0.65rem', textAlign: 'right' }}>
                        ID Opp: {opportunity.id?.substring(0, 8)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', fontSize: '0.65rem', textAlign: 'right' }}>
                        ID Match: {opportunity.matchId?.substring(0, 8)}
                      </Typography>
                    </Box>
                  </Box>
                </S.OpportunityCardLive>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  // Renderiza conteúdo de todas as oportunidades
  const renderAllOpportunities = () => {
    if (!debugData || !debugData.bettingOpportunities || debugData.bettingOpportunities.length === 0) {
      return (
        <S.NoDataContainer>
          <MdInfo size={48} color="#757575" />
          <Typography variant="h6" color="text.secondary">
            Nenhuma oportunidade de aposta agendada disponível
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Não há jogos agendados com oportunidades de apostas identificadas pelo nosso sistema.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchDebugData}
            startIcon={<MdOutlineQueryStats />}
          >
            TENTAR NOVAMENTE
          </Button>
        </S.NoDataContainer>
      );
    }

    return (
      <Grid container spacing={2}>
        {debugData.bettingOpportunities.slice(0, 9).map((opportunity, index) => {
          const valueScore = typeof opportunity.valueAssessment === 'number'
            ? opportunity.valueAssessment
            : 0;
          const confidenceScore = typeof opportunity.confidenceScore === 'number'
            ? opportunity.confidenceScore
            : 0;
          const odds = typeof opportunity.odds === 'number'
            ? opportunity.odds
            : 0;

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <S.OpportunityCard valueScore={valueScore}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1">{opportunity.league || "Liga não especificada"}</Typography>
                  {opportunity.isLive && <S.LiveBadge>AO VIVO</S.LiveBadge>}
                  {!opportunity.isLive && (
                    <S.ValueBadge value={valueScore}>
                      {valueScore.toFixed(1)}% valor
                    </S.ValueBadge>
                  )}
                </Box>

                <Typography variant="body2" gutterBottom>
                  {opportunity.homeTeamName || "Time Casa"} vs {opportunity.awayTeamName || "Time Visitante"}
                </Typography>

                {opportunity.currentScore && opportunity.isLive && (
                  <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    {opportunity.currentScore.home} - {opportunity.currentScore.away}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2"><strong>Aposta:</strong> {opportunity.betType || "N/D"}</Typography>
                  <Typography variant="body2"><strong>Seleção:</strong> {opportunity.betOption || "N/D"}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2"><strong>Odds:</strong> {odds.toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Confiança:</strong> {confidenceScore.toFixed(0)}%</Typography>
                </Box>

                {opportunity.reasons && opportunity.reasons.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="body2"><strong>Justificativa:</strong></Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {opportunity.reasons[0]}
                    </Typography>
                  </Box>
                )}
              </S.OpportunityCard>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Renderiza conteúdo de análise de partidas
  const renderMatchAnalysis = () => {
    if (!debugData?.matchPredictions || debugData.matchPredictions.length === 0) {
      return (
        <S.EmptyContainer>
          <Typography>Nenhuma análise de partida disponível</Typography>
        </S.EmptyContainer>
      );
    }

    return (
      <Grid container spacing={2}>
        {debugData.matchPredictions.map((prediction, index) => {
          const expectedGoals = getExpectedGoals(prediction);
          const homeWinProb = typeof prediction.homeWinProbability === 'number'
            ? prediction.homeWinProbability
            : 0;
          const drawProb = typeof prediction.drawProbability === 'number'
            ? prediction.drawProbability
            : 0;
          const awayWinProb = typeof prediction.awayWinProbability === 'number'
            ? prediction.awayWinProbability
            : 0;

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <S.MatchCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1">{prediction.leagueName || "Liga não especificada"}</Typography>
                  <Typography variant="body2">
                    {prediction.matchTime ? new Date(prediction.matchTime).toLocaleString() : 'Data não disponível'}
                  </Typography>
                </Box>

                <S.Teams>
                  <S.Team>
                    <Typography variant="body1">{prediction.homeTeamName || "Time Casa"}</Typography>
                  </S.Team>
                  <Typography variant="body2">vs</Typography>
                  <S.Team>
                    <Typography variant="body1">{prediction.awayTeamName || "Time Visitante"}</Typography>
                  </S.Team>
                </S.Teams>

                <Box mt={2}>
                  <WhoWins
                    homeTeamPercentage={homeWinProb}
                    draw={drawProb}
                    awayTeamPercentage={awayWinProb}
                  />
                </Box>

                <Box mt={2}>
                  <Typography variant="body2">
                    <strong>Placar Previsto:</strong> {prediction.predictedScore?.homeScore || 0} - {prediction.predictedScore?.awayScore || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Gols Esperados:</strong> {expectedGoals.home} - {expectedGoals.away}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Confiança:</strong> {typeof prediction.confidenceScore === 'number' ? prediction.confidenceScore.toFixed(0) : 0}%
                  </Typography>
                </Box>
              </S.MatchCard>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Função para buscar análises detalhadas
  const fetchDetailedAnalysis = async (matchId?: string) => {
    setIsLoadingDetailedAnalysis(true);
    setDetailedAnalysisError(null);

    try {
      const endpoint = matchId
        ? `/api/v1/smart-tickets/detailed-analysis/${matchId}`
        : '/api/v1/smart-tickets/detailed-analysis';

      const response = await api.get(endpoint);
      if (response && response.data) {
        console.log('Dados de análises detalhadas recebidos:', response.data);

        // Processar os dados recebidos
        const detailedData = { ...response.data };

        // Garantir que timestamp existe
        if (!detailedData.timestamp) {
          detailedData.timestamp = new Date().toISOString();
        }

        // Processar os matches, adicionando logos se necessário
        if (detailedData.matches && Array.isArray(detailedData.matches)) {
          detailedData.matches = detailedData.matches.map((match: DetailedMatch) => ({
            ...match,
            homeTeam: {
              ...match.homeTeam,
              logo: match.homeTeam.logo || getTeamLogo(match.homeTeam.name)
            },
            awayTeam: {
              ...match.awayTeam,
              logo: match.awayTeam.logo || getTeamLogo(match.awayTeam.name)
            }
          }));
        } else {
          detailedData.matches = [];
        }

        setDetailedAnalysis(detailedData as DetailedAnalysis);
        setIsLoadingDetailedAnalysis(false);
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('Erro ao obter análises detalhadas:', error);
      setDetailedAnalysisError("Erro ao carregar análises detalhadas. Por favor, tente novamente.");
      setIsLoadingDetailedAnalysis(false);
    }
  };

  // Carregar análises detalhadas quando o componente montar
  useEffect(() => {
    fetchDetailedAnalysis();
  }, []);

  // Renderiza as análises detalhadas das partidas
  const renderDetailedAnalysis = () => {
    if (isLoadingDetailedAnalysis) {
      return (
        <S.LoadingContainer>
          <CircularProgress />
          <Typography variant="body1">Carregando análises detalhadas...</Typography>
        </S.LoadingContainer>
      );
    }

    if (detailedAnalysisError) {
      return (
        <S.ErrorContainer>
          <Typography variant="h6" color="error">
            {detailedAnalysisError}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Por favor, tente novamente.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchDetailedAnalysis()}
            startIcon={<MdOutlineQueryStats />}
          >
            ATUALIZAR ANÁLISES
          </Button>
        </S.ErrorContainer>
      );
    }

    if (!detailedAnalysis || !detailedAnalysis.matches || detailedAnalysis.matches.length === 0) {
      return (
        <S.EmptyDataContainer>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Nenhuma análise detalhada disponível no momento
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Não há partidas com análises detalhadas disponíveis. Tente novamente mais tarde.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchDetailedAnalysis()}
            startIcon={<MdOutlineQueryStats />}
          >
            ATUALIZAR ANÁLISES
          </Button>
        </S.EmptyDataContainer>
      );
    }

    // Renderizar os cartões de análise detalhada
    return (
      <Grid container spacing={3}>
        {detailedAnalysis.matches.map((match) => (
          <Grid item xs={12} md={6} lg={4} key={match.matchId}>
            <S.DetailedMatchCard>
              {/* Cabeçalho com informações principais da partida */}
              <S.DetailedMatchHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {match.league.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={match.isLive ? `AO VIVO ${match.matchMinute}'` : match.status}
                    color={match.isLive ? "error" : "default"}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </Box>

                <S.TeamsContainer>
                  <S.TeamSection>
                    <S.TeamLogo src={match.homeTeam.logo} alt={match.homeTeam.name} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {match.homeTeam.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={match.homeTeam.recentForm || 'WWDLW'}
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        mt: 0.5
                      }}
                    />
                  </S.TeamSection>

                  <S.ScoreSection>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {match.score.home} - {match.score.away}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {match.date} {match.time}
                    </Typography>
                  </S.ScoreSection>

                  <S.TeamSection>
                    <S.TeamLogo src={match.awayTeam.logo} alt={match.awayTeam.name} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {match.awayTeam.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={match.awayTeam.recentForm || 'LDWDW'}
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        mt: 0.5
                      }}
                    />
                  </S.TeamSection>
                </S.TeamsContainer>
              </S.DetailedMatchHeader>

              {/* Seção de abas para as diferentes análises */}
              <Box sx={{ mt: 2 }}>
                <Tabs
                  value={0}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 30,
                    '& .MuiTab-root': {
                      minHeight: 30,
                      fontSize: '0.7rem',
                      py: 0
                    }
                  }}
                >
                  <Tab label="Oportunidades" sx={{ fontSize: '0.7rem' }} />
                  <Tab label="Estatísticas" sx={{ fontSize: '0.7rem' }} />
                  <Tab label="H2H" sx={{ fontSize: '0.7rem' }} />
                  <Tab label="Previsões" sx={{ fontSize: '0.7rem' }} />
                </Tabs>
              </Box>

              {/* Oportunidades de apostas */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                  Oportunidades ao Vivo ({match.liveOpportunities.length})
                </Typography>

                {match.liveOpportunities.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    {match.liveOpportunities.slice(0, 2).map((opp) => (
                      <S.OpportunityItem key={opp.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                              {opp.betType}: <span style={{ color: '#4fc3f7' }}>{opp.betOption}</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                              {opp.reasons[0]}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                              {opp.odds}
                            </Typography>
                            <Chip
                              size="small"
                              label={opp.riskLevel}
                              color={getRiskColor(opp.riskLevel)}
                              sx={{ height: 18, fontSize: '0.6rem' }}
                            />
                          </Box>
                        </Box>
                      </S.OpportunityItem>
                    ))}

                    {match.liveOpportunities.length > 2 && (
                      <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', textAlign: 'center', cursor: 'pointer', mt: 1 }}>
                        + {match.liveOpportunities.length - 2} mais oportunidades
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    Nenhuma oportunidade ao vivo disponível
                  </Typography>
                )}

                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', mt: 2 }}>
                  Oportunidades Pré-Jogo ({match.preMatchOpportunities.length})
                </Typography>

                {match.preMatchOpportunities.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    {match.preMatchOpportunities.slice(0, 2).map((opp) => (
                      <S.OpportunityItem key={opp.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                              {opp.betType}: <span style={{ color: '#ff9800' }}>{opp.betOption}</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                              {opp.reasons[0]}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                              {opp.odds}
                            </Typography>
                            <Chip
                              size="small"
                              label={opp.riskLevel}
                              color={getRiskColor(opp.riskLevel)}
                              sx={{ height: 18, fontSize: '0.6rem' }}
                            />
                          </Box>
                        </Box>
                      </S.OpportunityItem>
                    ))}

                    {match.preMatchOpportunities.length > 2 && (
                      <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', textAlign: 'center', cursor: 'pointer', mt: 1 }}>
                        + {match.preMatchOpportunities.length - 2} mais oportunidades
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    Nenhuma oportunidade pré-jogo disponível
                  </Typography>
                )}
              </Box>

              {/* Métricas de desempenho */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                  Métricas de Desempenho
                </Typography>

                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={4}>
                    <S.MetricBox>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                        Posse
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{match.performance.possession.home}%</Typography>
                        <Typography variant="body2">{match.performance.possession.away}%</Typography>
                      </Box>
                    </S.MetricBox>
                  </Grid>

                  <Grid item xs={4}>
                    <S.MetricBox>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                        Chutes
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{match.performance.shots.home.total}</Typography>
                        <Typography variant="body2">{match.performance.shots.away.total}</Typography>
                      </Box>
                    </S.MetricBox>
                  </Grid>

                  <Grid item xs={4}>
                    <S.MetricBox>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                        Escanteios
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{match.performance.corners.home}</Typography>
                        <Typography variant="body2">{match.performance.corners.away}</Typography>
                      </Box>
                    </S.MetricBox>
                  </Grid>
                </Grid>
              </Box>

              {/* Probabilidades */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                  Probabilidades
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <WhoWins
                    homeTeamPercentage={Math.round(match.predictions.winProbability.home * 100)}
                    draw={Math.round(match.predictions.winProbability.draw * 100)}
                    awayTeamPercentage={Math.round(match.predictions.winProbability.away * 100)}
                  />
                </Box>

                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <S.MetricBox>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                        Ambas marcam
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Sim: {Math.round(match.predictions.btts.yes * 100)}%</Typography>
                        <Typography variant="body2">Não: {Math.round(match.predictions.btts.no * 100)}%</Typography>
                      </Box>
                    </S.MetricBox>
                  </Grid>

                  <Grid item xs={6}>
                    <S.MetricBox>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem' }}>
                        Over/Under 2.5
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Over: {Math.round(match.predictions.overUnder.over25 * 100)}%</Typography>
                        <Typography variant="body2">Under: {Math.round(100 - match.predictions.overUnder.over25 * 100)}%</Typography>
                      </Box>
                    </S.MetricBox>
                  </Grid>
                </Grid>
              </Box>

              {/* Botão para análise completa */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => { }} // Implementar modal de análise detalhada
                  sx={{ fontSize: '0.7rem' }}
                >
                  VER ANÁLISE COMPLETA
                </Button>
              </Box>
            </S.DetailedMatchCard>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <S.Container>
      <S.Header>
        <Typography variant="h5" component="h1" gutterBottom>
          Bilhetes Inteligentes
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchDebugData}
            startIcon={<MdOutlineQueryStats />}
            size="small"
          >
            ATUALIZAR DADOS
          </Button>
        </Box>
      </S.Header>

      {isLoading ? (
        <S.LoadingContainer>
          <CircularProgress />
          <Typography variant="body1">Carregando dados de bilhetes inteligentes...</Typography>
        </S.LoadingContainer>
      ) : error ? (
        <S.ErrorContainer>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Por favor, tente novamente.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchDebugData}
            startIcon={<MdOutlineQueryStats />}
          >
            TENTAR NOVAMENTE
          </Button>
        </S.ErrorContainer>
      ) : debugData ? (
        <S.ContentContainer>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { color: 'text.secondary' },
                '& .Mui-selected': { color: 'primary.main' },
                '& .MuiTabs-indicator': { backgroundColor: 'primary.main' }
              }}
            >
              <Tab label="Oportunidades ao Vivo" />
              <Tab label="Todas Oportunidades" />
              <Tab label="Análise de Partidas" />
              <Tab label="Análises Detalhadas" />
              <Tab label="Sistema" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Card sx={{ bgcolor: '#242c35', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="text.primary">Oportunidades ao Vivo</Typography>
                <Divider sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                {renderLiveOpportunities()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Card sx={{ bgcolor: '#242c35', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="text.primary">Todas as Oportunidades</Typography>
                <Divider sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                {renderAllOpportunities()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Card sx={{ bgcolor: '#242c35', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="text.primary">Análise de Partidas</Typography>
                <Divider sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                {renderMatchAnalysis()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Card sx={{ bgcolor: '#242c35', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="text.primary">Análises Detalhadas</Typography>
                <Divider sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                {renderDetailedAnalysis()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Card sx={{ bgcolor: '#242c35', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="text.primary">Informações do Sistema</Typography>
                <Divider sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Tempo de Processamento:</strong> {debugData.processingTimeMs} ms
                </Typography>
                {debugData.version && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Versão do Sistema:</strong> {debugData.version}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>Data e Hora da Solicitação:</strong> {new Date(debugData.timestamp).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </TabPanel>
        </S.ContentContainer>
      ) : (
        <S.EmptyContainer>
          <Typography color="text.secondary">Nenhum dado disponível</Typography>
          <Button variant="outlined" onClick={fetchDebugData}>Carregar Dados</Button>
        </S.EmptyContainer>
      )}

      {/* Debug section - only visible in development */}
      {typeof window !== 'undefined' && window.location.hostname === 'localhost' && debugData && (
        <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(0,0,0,0.7)', borderRadius: 2, overflow: 'auto', maxHeight: '300px' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Debug Data Structure
          </Typography>
          <pre style={{ color: '#aaa', fontSize: '12px' }}>
            {JSON.stringify({
              timestamp: debugData.timestamp,
              lastUpdated: debugData.lastUpdated,
              liveBettingOpportunities: debugData.liveBettingOpportunities ?
                `Array(${debugData.liveBettingOpportunities.length})` : 'undefined',
              bettingOpportunities: debugData.bettingOpportunities ?
                `Array(${debugData.bettingOpportunities.length})` : 'undefined',
              scheduledBettingOpportunities: debugData.scheduledBettingOpportunities ?
                `Array(${debugData.scheduledBettingOpportunities.length})` : 'undefined',
              performanceMetrics: debugData.performanceMetrics,
              processingTimeMs: debugData.processingTimeMs,
            }, null, 2)}
          </pre>
        </Box>
      )}
    </S.Container>
  );
}